import { Injectable } from '@angular/core';
import { assertOptions, HttpClient } from '@beecore/api';
import { AuthService, ILoginStrategy, User, UserService } from '@beecore/auth';
import { AuthError, bindValues, cast, ConfigService, IAbpApiResponse } from '@beecore/core';
import { catchError, first, map } from 'rxjs/operators';
import { SfeAuthResponseEntity } from '../entities/sfe-auth-response.entity';
import { SfeAuthEntity } from '../entities/sfe-auth.entity';
import { SfeUserEntity } from '../entities/sfe-user.entity';
import { SfeAuthErrorHandlerService } from './sfe-auth-error-handler.service';

@Injectable({
  providedIn: 'root'
})
export class SfeLoginService
  implements ILoginStrategy<SfeAuthEntity> {

  private readonly authPath = ':abpAuthUri/auth';
  private readonly sessionPath = ':abpAuthUri/auth/session';

  constructor(
    private readonly $http: HttpClient,
    private readonly $config: ConfigService,
    private readonly $auth: AuthService,
    private readonly $user: UserService,
    private readonly $errorHandler: SfeAuthErrorHandlerService
  ) { }

  /** Efetua login em uma aplicação ABP utilizando credenciais de acesso. */
  async login(data?: SfeAuthEntity): Promise<void> {
    const { accessToken } = await this.loginRequest(data);

    this.$auth.setToken(accessToken);
  }

  async authorize(): Promise<void> {
    const sfeUser = await this.sessionRequest(`${this.$auth.getToken()}`);
    if (!sfeUser) {
      throw new AuthError('Invalid Token', 401);
    }

    const userData = new User(
      {
        id: sfeUser.id,
        userName: sfeUser.userName,
        name: sfeUser.name,
        surname: sfeUser.surname,
        email: sfeUser.emailAddress
      });

    this.$user.changeCurrentUser(userData);
  }

  async logout(): Promise<void> {
    await this.$auth.logout();
  }

  /** Realiza a criação de uma nova sessão. */
  private loginRequest(input: SfeAuthEntity): Promise<SfeAuthResponseEntity> {
    return this.$http
      .post<IAbpApiResponse<SfeAuthResponseEntity>>(
        `${bindValues(this.authPath, this.$config.config)}`,
        input
      )
      .pipe(
        first(),
        map(res => cast(SfeAuthResponseEntity, res.result) as SfeAuthResponseEntity),
        catchError(error => this.$errorHandler.handleErrors(error))
      )
      .toPromise();
  }

  /** Pega os dados da sessão ativa. */
  private sessionRequest(token: string): Promise<SfeUserEntity> {
    return this.$http
      .get<IAbpApiResponse<{ user: SfeUserEntity }>>(
        `${bindValues(this.sessionPath, this.$config.config)}`,
        assertOptions({ headers: { Authorization: `Bearer ${token}` } })
      )
      .pipe(
        first(),
        map(res => cast(SfeUserEntity, res.result?.user)),
        catchError(error => this.$errorHandler.handleErrors(error))
      )
      .toPromise();
  }

}
